class ModalSubscribe {
  constructor() {
    this.modal = document.querySelector('[data-modal-subscribe]');
    if (!this.modal) return;

    this.overlay = this.modal.querySelector('[data-modal-overlay]');
    this.closeButton = this.modal.querySelector('[data-modal-close]');
    this.form = this.modal.querySelector('#modal-newsletter-form');
    this.submitButton = this.modal.querySelector('[data-modal-submit]');
    this.successMessage = this.modal.querySelector('[data-modal-success]');
    this.errorMessage = this.modal.querySelector('#ModalNewsletter-error');
    this.errorMessageText = this.modal.querySelector('[data-modal-error-text]');
    this.storageKey = 'modalSubscribeShown';
    this.pendingKey = 'modalSubscribePendingSubmit';
    this.delayMs = this.getDelayFromSchema();
    this.isSubmitting = false;
    this._delayTimer = null;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.resetMessages();

    if (this.isCaptchaOrChallengePage()) {
      this.modal.classList.add('d-none');
      document.body.classList.remove('modal-open');
      return;
    }

    if (this.hasModalHash()) {
      this.showModal();

      if (this.isModalPostedSuccessfully()) {
        this.showSuccessMessage();
        this.markAsShown();
        this.clearPendingSubmit();
      } else if (this.isModalPostedWithErrors()) {
        this.showErrorMessage(this.getCurrentErrorMessage() || 'Please enter a valid email address.');
        this.clearPendingSubmit();
      } else if (this.consumePendingSubmit()) {
        this.showSuccessMessage();
        this.markAsShown();
      } else {
        this.showFormState();
      }

      this.cleanupUrlState();
      return;
    }

    if (this.hasBeenShown()) return;

    if (document.readyState === 'complete') {
      this.startDelay();
    } else {
      window.addEventListener('load', () => this.startDelay(), { once: true });
    }
  }

  getDelayFromSchema() {
    const delayAttr = this.modal.getAttribute('data-delay');
    if (delayAttr !== null && delayAttr !== '') {
      const n = parseInt(delayAttr, 10);
      return Number.isFinite(n) ? n * 1000 : 6000;
    }
    return 6000;
  }

  startDelay() {
    this._delayTimer = setTimeout(() => this.showModal(), this.delayMs);
  }

  isCaptchaOrChallengePage() {
    const pathname = (window.location.pathname || '').toLowerCase();
    const search = (window.location.search || '').toLowerCase();

    if (pathname.includes('/challenge') || pathname.includes('/captcha')) {
      return true;
    }

    if (search.includes('challenge=') || search.includes('captcha')) {
      return true;
    }

    return false;
  }

  setupEventListeners() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeModal());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeModal());
    }

    document.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && !this.modal.classList.contains('d-none')) {
      this.closeModal();
    }
  }

  async handleSubmit(event) {
    if (!this.form || this.isSubmitting) return;
    if (!this.form.reportValidity()) return;

    this.setSubmittingState(true);
    this.showFormState();
    this.setPendingSubmit();
  }

  hasModalHash() {
    const hash = (window.location.hash || '').toLowerCase();
    return hash.includes('modal-newsletter-form');
  }

  isModalPostedSuccessfully() {
    const params = new URLSearchParams(window.location.search);
    const posted = params.get('customer_posted') === 'true';
    const hash = (window.location.hash || '').toLowerCase();
    return posted && hash.includes('modal-newsletter-form');
  }

  isModalPostedWithErrors() {
    if (!this.hasModalHash()) {
      return false;
    }

    if (!this.form) {
      return false;
    }

    return Boolean(this.form.querySelector('[aria-invalid="true"]'));
  }

  getCurrentErrorMessage() {
    if (!this.errorMessageText) {
      return '';
    }
    return (this.errorMessageText.textContent || '').trim();
  }

  cleanupUrlState() {
    if (!window.history || typeof window.history.replaceState !== 'function') {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
  }

  setSubmittingState(isSubmitting) {
    this.isSubmitting = isSubmitting;
    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }
  }

  showSuccessMessage() {
    if (this.successMessage) this.successMessage.classList.remove('d-none');
    if (this.form) this.form.classList.add('d-none');
    this.hideErrorMessage();
  }

  showErrorMessage(message) {
    if (this.successMessage) this.successMessage.classList.add('d-none');
    if (this.form) this.form.classList.remove('d-none');
    if (!this.errorMessage) return;
    this.errorMessage.classList.remove('d-none');
    if (this.errorMessageText) {
      this.errorMessageText.textContent = message;
    } else {
      this.errorMessage.textContent = message;
    }
  }

  showFormState() {
    if (this.form) this.form.classList.remove('d-none');
    if (this.successMessage) this.successMessage.classList.add('d-none');
    this.hideErrorMessage();
  }

  resetMessages() {
    this.showFormState();
  }

  hideErrorMessage() {
    if (!this.errorMessage) return;
    this.errorMessage.classList.add('d-none');
  }

  showModal() {
    if (!this.modal) return;
    if (!this.modal.classList.contains('d-none')) return;
    this.modal.classList.remove('d-none');
    document.body.classList.add('modal-open');

    const emailInput = this.modal.querySelector('input[name="contact[email]"], textarea[name="contact[email]"], #ModalNewsletterForm');
    if (emailInput && typeof emailInput.focus === 'function') {
      setTimeout(() => emailInput.focus(), 300);
    }
  }

  closeModal() {
    if (!this.modal) return;
    clearTimeout(this._delayTimer);
    this.modal.classList.add('d-none');
    document.body.classList.remove('modal-open');
    this.markAsShown();
  }

  hasBeenShown() {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch (e) {
      return true;
    }
  }

  markAsShown() {
    try {
      localStorage.setItem(this.storageKey, 'true');
    } catch (e) {
    }
  }

  setPendingSubmit() {
    try {
      sessionStorage.setItem(this.pendingKey, String(Date.now()));
    } catch (e) {
    }
  }

  clearPendingSubmit() {
    try {
      sessionStorage.removeItem(this.pendingKey);
    } catch (e) {
    }
  }

  consumePendingSubmit() {
    try {
      const value = sessionStorage.getItem(this.pendingKey);
      if (!value) {
        return false;
      }

      sessionStorage.removeItem(this.pendingKey);

      const timestamp = parseInt(value, 10);
      if (!Number.isFinite(timestamp)) {
        return true;
      }

      const ageMs = Date.now() - timestamp;
      return ageMs >= 0 && ageMs < 5 * 60 * 1000;
    } catch (e) {
      return false;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ModalSubscribe());
} else {
  new ModalSubscribe();
}